import { resolveTransform, type Ease, type MotionTrack } from '@chapsi/ui-tokens/motion';

/**
 * Plays the shared motion tracks (packages/ui-tokens/src/motion.ts) with the Web Animations API: transform
 * and opacity only (plus the status ring's dash), so the compositor does the work and nothing reflows.
 */

const REDUCE = '(prefers-reduced-motion: reduce)';

export function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(REDUCE).matches;
  } catch {
    return false;
  }
}

export function subscribeReducedMotion(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => undefined;
  const mq = window.matchMedia(REDUCE);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

export function cssEase(ease: Ease): string {
  return ease === 'linear' ? 'linear' : `cubic-bezier(${ease.join(', ')})`;
}

export interface KeyframeOptions {
  /** Multiplies translations (a design measured in px drawn at another size). */
  scale?: number;
  /** The status ring's dash length, for `d` keys. */
  dash?: number;
}

/** A track as WAAPI keyframes. Like CSS, a property missing from a keyframe is interpolated past it. */
export function toKeyframes(track: MotionTrack, { scale = 1, dash = 0 }: KeyframeOptions = {}): Keyframe[] {
  const unit = track.pct ? '%' : 'px';
  const ease = cssEase(track.ease);
  return track.keys.map((k) => {
    const frame: Keyframe = { offset: k.at, easing: ease };
    if (k.t !== undefined) {
      const t = resolveTransform(k.t);
      const s = track.pct ? 1 : scale;
      frame.transform = `translate(${round(t.x * s)}${unit}, ${round(t.y * s)}${unit}) scale(${round(t.sx)}, ${round(t.sy)}) rotate(${round(t.r)}deg)`;
    }
    if (k.o !== undefined) frame.opacity = k.o;
    if (k.d !== undefined) frame.strokeDashoffset = String(round(k.d * dash));
    return frame;
  });
}

const round = (n: number) => Math.round(n * 1000) / 1000;

export interface PlayOptions extends KeyframeOptions {
  delay?: number;
  /** Plays faster (2) or slower (0.5) than designed. */
  speed?: number;
  /** `Infinity` loops. */
  iterations?: number;
  alternate?: boolean;
  /** Pivot in the SVG's own units; the element turns and scales around it. */
  pivot?: { x: number; y: number };
}

/** Starts one track on one element. Returns null where the API is missing (old browsers, tests). */
export function play(el: Element | null | undefined, track: MotionTrack, opts: PlayOptions = {}): Animation | null {
  if (!el || typeof (el as HTMLElement).animate !== 'function') return null;
  if (opts.pivot) setPivot(el, opts.pivot);
  return (el as HTMLElement).animate(toKeyframes(track, opts), {
    duration: track.ms / (opts.speed ?? 1),
    delay: opts.delay ?? 0,
    iterations: opts.iterations ?? 1,
    direction: opts.alternate ? 'alternate' : 'normal',
    // Delayed parts hold their first frame until they start; when done, everything is back at rest.
    fill: 'backwards',
  });
}

/**
 * Puts an SVG layer's transform origin on a point in the drawing's units. The origin is measured from
 * the layer's own box (`fill-box`), which every browser agrees on.
 */
export function setPivot(el: Element, pivot: { x: number; y: number }): void {
  const g = el as SVGGraphicsElement & { style: CSSStyleDeclaration };
  if (typeof g.getBBox !== 'function') return;
  try {
    const box = g.getBBox();
    g.style.transformBox = 'fill-box';
    g.style.transformOrigin = `${round(pivot.x - box.x)}px ${round(pivot.y - box.y)}px`;
  } catch {
    // Not rendered yet (display: none): the animation still plays, around the default origin.
  }
}

export function stopAll(animations: readonly (Animation | null)[]): void {
  for (const a of animations) a?.cancel();
}
